import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingRondeboschPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Rondebosch Professional Painters - Residential & Commercial Painting | Heritage Specialists',
    description: 'Expert painting services in Rondebosch, Newlands, and Claremont. Specialized in residential, commercial, and heritage property painting. Over 16 years serving Southern Suburbs with quality craftsmanship.',
    keywords: 'painters Rondebosch, house painters Rondebosch, residential painting Rondebosch, commercial painting, heritage painting, Southern Suburbs painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Rondebosch',
          description: 'Professional residential and heritage painting',
          address: 'Rondebosch, Cape Town',
          priceRange: '$$',
          serviceArea: ['Rondebosch', 'Newlands', 'Claremont', 'Mowbray', 'Rosebank', 'Observatory']
        }),
        generateServiceSchema({
          name: 'Residential & Heritage Painting',
          description: 'Complete painting solutions including heritage properties',
          provider: 'Painters Rondebosch',
          areaServed: ['Rondebosch', 'Newlands', 'Claremont'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Heritage Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Rondebosch",
    location: "Rondebosch, Cape Town",
    website: "https://paintersrondebosch.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/ff8cdce41b410c1e414d6cc9d5933698.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20restored%20Victorian%20house%20exterior%20with%20fresh%20white%20paint%20ornate%20trim%20work%20and%20heritage%20architectural%20features%20in%20leafy%20garden%20setting&width=800&height=600&seq=gallery-rondebosch1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20interior%20room%20painting%20with%20high%20ceilings%20ornate%20cornices%20and%20period%20features%20professional%20finish%20in%20heritage%20home&width=800&height=600&seq=gallery-rondebosch2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=modern%20interior%20painting%20project%20in%20contemporary%20home%20with%20clean%20lines%20neutral%20colors%20and%20professional%20finish&width=800&height=600&seq=gallery-rondebosch3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=exterior%20house%20painting%20with%20scaffolding%20on%20heritage%20property%20professional%20painters%20working%20on%20detailed%20trim%20and%20woodwork&width=800&height=600&seq=gallery-rondebosch4&orientation=landscape"
    ],
    services: [
      {
        title: "Heritage Home Painting",
        description: "Specialized painting services for Victorian and Edwardian heritage properties. We understand the unique requirements of period homes and use appropriate techniques and materials to preserve their character while providing lasting protection.",
        features: ["Period property restoration", "Ornate trim and cornices", "Heritage color matching", "Architectural detail preservation"]
      },
      {
        title: "Victorian Property Restoration",
        description: "Expert restoration painting for Victorian-era homes. Our team has extensive experience working with heritage properties, ensuring that all work respects the original architectural integrity while meeting modern standards.",
        features: ["Original feature restoration", "Period-appropriate finishes", "Decorative plasterwork", "Sash window painting"]
      },
      {
        title: "Modern Interior Painting",
        description: "Contemporary interior painting services for modern homes and renovated properties. We combine traditional craftsmanship with modern techniques to deliver flawless finishes.",
        features: ["Contemporary color schemes", "Feature walls", "Open-plan spaces", "Designer finishes"]
      }
    ],
    serviceAreas: ["Rondebosch", "Mowbray", "Observatory", "Rosebank", "Newlands", "Claremont"],
    about: "Painters Rondebosch is a premier painting company specializing in heritage and modern properties in Cape Town's Southern Suburbs. With over 15 years of experience, we have established ourselves as the trusted choice for homeowners who value quality craftsmanship and attention to detail. Our team combines traditional painting techniques with modern innovations to deliver exceptional results.",
    aboutExtended: [
      "Located in the heart of Rondebosch, we have deep roots in this historic suburb and understand the unique character of its properties. From grand Victorian mansions to contemporary family homes, we approach each project with the same dedication to excellence. Our painters are specially trained in heritage restoration techniques, ensuring that period properties receive the care and expertise they deserve.",
      "We pride ourselves on our meticulous approach to surface preparation, which is the foundation of any lasting paint job. Whether we're restoring ornate cornices on a Victorian home or creating a sleek modern finish in a contemporary space, we never compromise on quality. Our use of premium paints and materials ensures that your property not only looks beautiful but is also protected against Cape Town's variable weather conditions.",
      "What sets us apart is our commitment to understanding each client's vision and bringing it to life. We offer comprehensive color consultation services, helping you choose palettes that complement your home's architecture and your personal style. Our team works efficiently and respectfully, minimizing disruption to your daily life while delivering results that exceed expectations."
    ],
    ourApproach: [
      "Detailed heritage property assessment",
      "Comprehensive surface preparation and repairs",
      "Period-appropriate materials and techniques",
      "Expert color consultation and matching",
      "Meticulous application and finishing",
      "Thorough cleanup and final inspection"
    ],
    whyChoose: [
      "15+ years heritage painting experience",
      "Victorian property specialists",
      "Fully licensed and insured",
      "Premium quality materials",
      "Expert color consultation",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "Dr. James Robertson",
        rating: 5,
        text: "Outstanding work on our Victorian home! The team understood the importance of preserving the heritage features while giving the house a fresh new look. Their attention to detail was remarkable."
      },
      {
        name: "Sarah Mitchell",
        rating: 5,
        text: "We hired Painters Rondebosch for our modern home renovation and couldn't be happier. Professional, punctual, and the quality of work is exceptional. Highly recommended!"
      },
      {
        name: "Peter van Niekerk",
        rating: 5,
        text: "Excellent service from start to finish. They restored the ornate cornices and trim on our Edwardian home beautifully. True craftsmen who take pride in their work."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Rondebosch</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Rondebosch</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h4 className="text-2xl font-bold mb-6">Areas We Serve</h4>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in Cape Town's Southern Suburbs:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer specialized painting services for heritage and modern properties throughout Rondebosch and the Southern Suburbs.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Rondebosch, Mowbray, Observatory</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
